import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LayoutFixHome from "../components/LayoutFixHome";
import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import { RoutesConfig } from "../router/types";
import { toast } from "react-toastify";
import { addUser, getAllClients } from "../services/clientService";

export const AssociarCliente: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [allClientsLoaded, setAllClientsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resultAllClients, setResultAllClients] = useState<any[]>([]);
  const [value, setValue] = React.useState<any>(resultAllClients[0]);
  const [inputValue, setInputValue] = React.useState("");
  const [open, setOpen] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [failedEmails, setFailedEmails] = useState<any>([]);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleBack = () => {
    navigate(RoutesConfig.CADASTRARUSUARIO.path);
  };

  const fetchAllClientsOnce = async () => {
    let allClients: any[] = [];
    let cursor = null;
    let hasMoreResults = true;

    while (hasMoreResults) {
      const data: any = await getAllClients(cursor);
      allClients = [
        ...allClients,
        ...data.items.filter(
          (newClient: any) =>
            !allClients.some((c) => c.CODIGO === newClient.CODIGO)
        ),
      ];
      cursor = data.nextCursor;
      hasMoreResults = data.hasNextPage;
    }

    setResultAllClients(allClients);
    setAllClientsLoaded(true);
  };

  useEffect(() => {
    if (!allClientsLoaded) {
      setIsLoading(true);
      try {
        fetchAllClientsOnce();
      } catch (error) {
        toast.error("Erro ao carregar todos os clientes.");
      } finally {
        setIsLoading(false);
      }
    }
  }, []);

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setInputValue(text);
  };

  const filteredClients = React.useMemo(() => {
    if (!searchText.trim()) return resultAllClients;
    const lowerSearch = searchText.toLowerCase();
    return resultAllClients.filter((client) => {
      const label = `${client.NOME} - ${client.INSC_CNPJ}`.toLowerCase();
      return label.includes(lowerSearch);
    });
  }, [searchText, resultAllClients]);

  const addEmail = (email: string) => {
    const trimmed = email.trim();
    if (
      trimmed &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) &&
      !emails.includes(trimmed)
    ) {
      setEmails([...emails, trimmed]);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      e.preventDefault();
      if (inputValue) {
        addEmail(inputValue);
        setInputValue("");
      }
    }
    if (e.key === "Backspace" && !inputValue && emails.length) {
      setEmails(emails.slice(0, -1));
    }
  };

  const handleRemove = (email: string) => {
    setEmails(emails.filter((e) => e !== email));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData("text");
    const parts = paste.split(/[\s,;]+/);
    let added = false;
    parts.forEach((part) => {
      if (
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(part.trim()) &&
        !emails.includes(part.trim())
      ) {
        setEmails((prev) => [...prev, part.trim()]);
        added = true;
      }
    });
    if (added) setInputValue("");
  };

  useEffect(() => {
    if (isFirstLoad && resultAllClients && resultAllClients.length > 0) {
      setIsFirstLoad(false);
    }
  }, [resultAllClients]);

  const traduzirErro = (mensagemOriginal: string) => {
    if (!mensagemOriginal) return "Erro desconhecido.";

    const mapaErros: { [key: string]: string } = {
      "User already exists": "E-mail já cadastrado!",
      "Invalid email format": "Formato de e-mail inválido.",
      "User not authorized": "Usuário não autorizado.",
    };
    return mapaErros[mensagemOriginal] || mensagemOriginal;
  };

  const handleConcluir = async () => {
    if (!value || !value.INSC_CNPJ) {
      toast.error("Selecione uma empresa para continuar.");
      return;
    }
    const type = 3;

    try {
      const results = await Promise.all(
        emails.map(async (email) => {
          try {
            await addUser(email, value?.INSC_CNPJ, type);
            return { email, success: true };
          } catch (error: any) {
            const rawMessage =
              error?.response?.data?.message ||
              error?.message ||
              "Erro desconhecido";
            const message = traduzirErro(rawMessage);

            return { email, success: false, message };
          }
        })
      );

      const allSucceeded = results.every((r) => r.success);
      const failed = results.filter((r) => !r.success);
      setFailedEmails(failed);

      if (allSucceeded) {
        toast.success(
          `Todos os usuários da empresa ${value?.NOME?.split(" ")
            .slice(0, 2)
            .join(" ")} foram pré-cadastrados com sucesso!`
        );
        setValue(null);
        setEmails([]);
        setFailedEmails([]);
        setInputValue("");
        navigate(RoutesConfig.CADASTRARUSUARIO.path);
      } else {
        setEmails(failed.map((f) => f.email));
        toast.error(
          `Alguns usuários da empresa ${value?.NOME?.split(" ")
            .slice(0, 2)
            .join(" ")} não foram cadastrados!`
        );
      }
    } catch (error) {
      console.error("Erro inesperado:", error);
      toast.error("Erro inesperado ao cadastrar usuários clientes.");
    }
  };

  return (
    <>
      <LayoutFixHome>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: "29px",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
              marginTop: {
                sm: "75px",
                xs: "75px",
                md: 0,
              },
            }}
          >
            <div onClick={handleBack} style={{ cursor: "pointer" }}>
              <ExpandCircleDownOutlinedIcon
                style={{ transform: "rotate(-270deg)" }}
                sx={{ color: "#0A0C33", height: "30px", width: "30px" }}
              />
            </div>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "#0A0C33" }}
            >
              Associação de Cliente
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            border: "1px solid #0A0C33",
            height: {
              xs: "100vh",
              sm: "100vh",
              md: "81vh",
            },
            borderRadius: "15px",
            p: 3,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography
              sx={{ color: "#0A0C33", fontSize: "22px", fontWeight: 500 }}
            >
              Associados
            </Typography>
            <Typography sx={{ color: "#0A0C33", fontSize: "18px" }}>
              (um link de acesso será enviado para os e-mails realizarem o
              cadastro)
            </Typography>
          </Box>
          <Box
            sx={{
              position: "relative",
              width: { md: "45%", xs: "100%", sm: "100%" },
              marginTop: {
                sm: "16px",
                xs: "16px",
                md: "38px",
              },
              marginBottom: {
                sm: "16px",
                xs: "16px",
                md: "36px",
              },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                width: "24px",
                height: "100%",
                backgroundColor: "#326AFD",
                borderRadius: "12px 0px 0px 12px",
                zIndex: 1,
              }}
            />

            <Autocomplete
              key={value?.CODIGO}
              value={value}
              inputValue={searchText}
              loading={isFirstLoad}
              loadingText="Carregando..."
              onInputChange={(event, newInputValue) => {
                setSearchText(newInputValue);
              }}
              onChange={(event, newValue) => {
                setValue(newValue);
              }}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              options={filteredClients}
              getOptionLabel={(option: any) =>
                `${option.NOME} - ${option.INSC_CNPJ}`
              }
              // popupIcon={open ? <ArrowDropDownIcon /> : <SearchIcon />}
              PopperComponent={(props) => (
                <Popper
                  {...props}
                  placement="bottom-start"
                  modifiers={[
                    {
                      name: "offset",
                      options: {
                        offset: [0, 4],
                      },
                    },
                  ]}
                  sx={{
                    width: "45%",
                    marginLeft: "-24px",
                  }}
                />
              )}
              sx={{ width: "100%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Nome da Empresa"
                  sx={{
                    fontSize: "24px !important",
                    backgroundColor: "#F6F6F6",
                    borderRadius: "12px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      paddingLeft: "44px",
                      "& fieldset": {
                        border: "none",
                      },
                    },
                    "& label.Mui-focused": {
                      color: "#191818",
                      fontWeight: "600",
                    },
                  }}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                minHeight: 265,
                maxHeight: 265,
                border: "1px solid #0A0C33",
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {emails.map((email, idx) => {
                  const error = failedEmails.find(
                    (e: any) => e.email === email
                  );

                  return (
                    <Tooltip
                      title={error?.message || ""}
                      arrow
                      key={idx}
                      style={{ alignItems: "center" }}
                      PopperProps={{
                        modifiers: [
                          {
                            name: "zIndex",
                            enabled: true,
                            options: { zIndex: 9999 },
                          },
                        ],
                      }}
                    >
                      <Chip
                        label={email}
                        onDelete={() => handleRemove(email)}
                        sx={{
                          mb: 1,
                          backgroundColor: error ? "#ffe6e6" : "#326AFD",
                          color: error ? "#D32F2F" : "white",
                          border: error ? "1px solid #D32F2F" : "none",
                        }}
                      />
                    </Tooltip>
                  );
                })}
                <TextField
                  name="addCliente"
                  inputRef={inputRef}
                  type="email"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  onPaste={handlePaste}
                  placeholder="Insira o email"
                  label="Insira o email"
                  variant="standard"
                  autoComplete="off"
                  sx={{
                    minWidth: 180,
                    mb: 1,
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#326AFD",
                    },
                  }}
                />
              </Box>
            </Paper>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: "1px solid #eee",
              background: "#fff",
              borderRadius: "0 0 15px 15px",
            }}
          >
            <Button
              size="large"
              variant="outlined"
              onClick={() => {
                setEmails([]);
                setInputValue("");
                setValue("");
                setFailedEmails([]);
              }}
              sx={{
                textTransform: "none",
                borderRadius: "26px",
                border: "1px solid #0A0C33",
                color: "#0A0C33",
              }}
            >
              Limpar
            </Button>
            <Button
              size="large"
              variant="contained"
              onClick={handleConcluir}
              sx={{
                textTransform: "none",
                backgroundColor: "#326AFD",
                borderRadius: "26px",
                fontWeight: 800,
              }}
              disabled={emails.length === 0}
            >
              Concluir
            </Button>
          </Box>
        </Box>
      </LayoutFixHome>
    </>
  );
};
